import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Download } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

/**
 * <InstallPrompt /> — friendly "Add to Home Screen" banner.
 *
 * Behaviour (Feb 2026 · Return-Loop pass):
 *   • Post-success only — we now WAIT for a `we-activity-complete`
 *     window event before showing the banner. This means the child
 *     only sees the prompt in the moment of positive feeling, never
 *     on a cold landing.
 *   • Shown at most TWICE per device. After the second dismissal the
 *     banner is muted permanently on that device.
 *   • Never shows if already installed (display-mode standalone or the
 *     iOS `navigator.standalone` shim).
 *   • Compass voice: "Put My World Quest on your screen so you can
 *     find it tomorrow!"
 *   • Tracks install_prompt_shown · install_accepted · install_dismissed
 *     via the site-wide analytics helper.
 *   • iOS Safari never fires beforeinstallprompt — we show a soft
 *     "Add to Home Screen" instruction card instead, gated on the
 *     same we-activity-complete event.
 */
const SHOWN_KEY = "mwq_a2hs_shown_count";
const MAX_SHOWS = 2;
const INSTALLED_KEY = "mwq_a2hs_installed";

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone === true) return true;  // iOS Safari
  return false;
}

function isIos() {
  if (typeof window === "undefined") return false;
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
}

function shownCount() {
  return Number(localStorage.getItem(SHOWN_KEY) || 0);
}

function bumpShown() {
  localStorage.setItem(SHOWN_KEY, String(shownCount() + 1));
}

function muted() {
  if (localStorage.getItem(INSTALLED_KEY) === "1") return true;
  return shownCount() >= MAX_SHOWS;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [gated, setGated] = useState(true); // start hidden; unlock on success

  useEffect(() => {
    if (isStandalone() || muted()) return undefined;

    // Capture the deferred prompt when the browser offers it, but keep
    // the banner hidden until a success event fires.
    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    // Unlock the banner the FIRST time a real activity completes in
    // this session — that's the moment of positive feeling we want.
    const onSuccess = () => {
      if (muted()) return;
      setGated(false);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("we-activity-complete", onSuccess);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("we-activity-complete", onSuccess);
    };
  }, []);

  // Once unlocked on iOS (which never fires beforeinstallprompt), show
  // the iOS instruction card after a tiny delay.
  useEffect(() => {
    if (gated) return undefined;
    if (isIos() && !muted()) {
      const t = window.setTimeout(() => setShowIos(true), 500);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [gated]);

  // Emit `install_prompt_shown` the moment either banner becomes real.
  useEffect(() => {
    if ((deferred && !gated) || showIos) {
      try { trackEvent("install_prompt_shown", { platform: isIos() ? "ios" : "android" }); } catch { /* ignore */ }
      bumpShown();
    }
  }, [deferred, gated, showIos]);

  const dismiss = () => {
    try { trackEvent("install_dismissed", { platform: isIos() ? "ios" : "android" }); } catch { /* ignore */ }
    setDeferred(null);
    setShowIos(false);
    setGated(true);
  };

  const doInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "accepted") {
        localStorage.setItem(INSTALLED_KEY, "1");
        try { trackEvent("install_accepted", { platform: "android" }); } catch { /* ignore */ }
      } else {
        try { trackEvent("install_dismissed", { platform: "android" }); } catch { /* ignore */ }
      }
    } catch { /* swallow */ }
    setDeferred(null);
    setGated(true);
  };

  if (!gated && deferred) {
    return (
      <div
        role="dialog"
        aria-label="Install My World Quest"
        data-testid="a2hs-android"
        className="fixed inset-x-3 bottom-3 z-[80] md:left-auto md:right-4 md:w-96 rounded-2xl border-[3px] border-slate-900 bg-white shadow-[6px_6px_0_0_rgba(15,23,42,1)] p-3 flex items-center gap-3"
      >
        <img src="/logo192.png" alt="" width="40" height="40" className="rounded-lg" aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="font-heading text-sm font-bold text-slate-900 leading-tight">
            Put us on your screen!
          </p>
          <p className="text-[11px] text-slate-600 leading-tight">
            One-tap access tomorrow — works offline too.
          </p>
        </div>
        <button
          type="button"
          onClick={doInstall}
          data-testid="a2hs-install-btn"
          className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 bg-emerald-300 font-bold text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:bg-emerald-400"
        >
          <Download size={13} strokeWidth={3} /> Install
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="a2hs-dismiss"
          className="h-8 w-8 inline-flex items-center justify-center rounded-full border-2 border-slate-300 bg-white hover:bg-slate-50"
        >
          <X size={13} strokeWidth={3} />
        </button>
      </div>
    );
  }

  if (showIos) {
    return (
      <div
        role="dialog"
        aria-label="Add to Home Screen on iOS"
        data-testid="a2hs-ios"
        className="fixed inset-x-3 bottom-3 z-[80] rounded-2xl border-[3px] border-slate-900 bg-white shadow-[6px_6px_0_0_rgba(15,23,42,1)] p-3"
      >
        <div className="flex items-start gap-2">
          <img src="/logo192.png" alt="" width="36" height="36" className="rounded-lg mt-0.5" aria-hidden />
          <div className="flex-1 min-w-0">
            <p className="font-heading text-sm font-bold text-slate-900">
              Put us on your screen tomorrow
            </p>
            <p className="mt-0.5 text-[11px] text-slate-700 leading-snug">
              Tap the <span aria-label="share icon">⎋</span> <strong>Share</strong> button in
              Safari, then choose <strong>Add to Home Screen</strong>.
            </p>
            <Link
              to="/pwa-help"
              onClick={dismiss}
              className="mt-1 inline-block text-[11px] text-sky-600 underline font-semibold"
            >
              Show me how
            </Link>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="a2hs-dismiss"
            className="h-8 w-8 inline-flex items-center justify-center rounded-full border-2 border-slate-300 bg-white hover:bg-slate-50"
          >
            <X size={13} strokeWidth={3} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
